import React from 'react';
import PropTypes from 'prop-types';
import BootstrapTable from 'react-bootstrap-table-next';
import ModalWindow from '../../common/Modal';
import Button from '../../common/Button';
import StatusBadge from '../../common/StatusBadge';
import styles from './OrderSummaryModal.module.scss';
import { currencyFormat, dateFormatter, orderTypeFormatter } from '../../../utils/formatHelper';

function formatTotalQuantity(cell, row) {
  if (row.returned_quantity > 0) {
    return (
      <>
        <span className="cross">{cell}</span>
        <span>{cell - row.returned_quantity}</span>
      </>
    );
  }

  return <span>{cell}</span>;
}

function formatTotalValue(cell, row, currency) {
  const unitPrice = row.unit_price;

  if (row.returned_quantity > 0) {
    return (
      <>
        <span className="cross">{currencyFormat(unitPrice * row.quantity, currency)}</span>
        <span>{currencyFormat(unitPrice * (row.quantity - row.returned_quantity), currency)}</span>
      </>
    );
  }

  return currencyFormat(unitPrice * row.quantity, currency);
}

function OrderSummaryModal(props) {
  const trackingNumbers = [];
  let totalShipping = 0;
  let totalValue = 0;
  let totalQuantity = 0;
  let currency;

  const calculateRows = () => {
    const {
      suppliersNames,
      order: {
        info: { fulfillments },
      },
    } = props;
    const variants = {};

    fulfillments.forEach((fulfillment) => {
      const { fulfillment_lines: lines, fulfillment_returns: returns, tracking_number, supplier_id } = fulfillment;

      lines.forEach((line) => {
        const {
          quantity,
          shipping_price: shippingPrice,
          handling_price: handlingPrice,
          unit_price: unitPrice,
          variant_id: variantId,
        } = line;

        totalShipping += (shippingPrice + handlingPrice) * quantity;
        totalValue += (shippingPrice + unitPrice + handlingPrice) * quantity;
        totalQuantity += quantity;

        line.supplier_name = suppliersNames[supplier_id];

        if (!variants.hasOwnProperty(variantId)) {
          variants[variantId] = line;
        } else {
          variants[variantId].quantity += quantity;
        }

        if (!currency) {
          currency = line.variant.currency;
        }
      });

      // Check returns and add `returned_quantity` to lines
      returns.forEach(({ fulfillment_return_lines }) => {
        fulfillment_return_lines.forEach((returnLine) => {
          const line = lines.find(({ id }) => id === returnLine.fulfillment_line_id);
          if (line) {
            const { variant_id: variantId } = line;
            if (!variants[variantId].returned_quantity) {
              variants[variantId].returned_quantity = 0;
            }

            variants[variantId].returned_quantity += returnLine.quantity;
          }
        });
      });

      if (tracking_number) {
        trackingNumbers.push(tracking_number);
      }
    });

    return Object.values(variants);
  };

  const { opened, onClose, order } = props;

  if (!order) {
    return null;
  }

  const { created_at, retailer_order_id, type, store, sales_order_number } = order;

  const rows = calculateRows();
  const showTrackingNumbers = trackingNumbers.length > 0;

  const columns = [
    {
      text: 'Supplier',
      dataField: 'supplier_name',
    },
    {
      text: 'Title',
      dataField: 'variant.title',
    },
    {
      text: 'SKU',
      dataField: 'variant.sku',
    },
    {
      text: 'Unit Price',
      dataField: 'unit_price',
      formatter: (cell) => currencyFormat(cell, currency),
    },
    {
      text: 'Quantity',
      dataField: 'quantity',
      formatter: formatTotalQuantity,
    },
    {
      text: 'Total Cost',
      dataField: 'total_cost',
      isDummyField: true,
      formatter: (cell, row) => formatTotalValue(cell, row, currency),
    },
  ];

  const noDataIndication = () => `There are no products to display`;

  return (
    <ModalWindow open={opened} onClose={props.onClose}>
      <h2>
        Order Summary <span>{retailer_order_id}</span>
      </h2>
      <hr />

      <div className={styles.info}>
        <dl>
          <dt>ID:</dt>
          <dd>{retailer_order_id}</dd>
          {sales_order_number && (
            <>
              <dt>Sales Order Number:</dt>
              <dd>{sales_order_number}</dd>
            </>
          )}
          <dt>Date:</dt>
          <dd>{dateFormatter(created_at)}</dd>
        </dl>
        <dl>
          <dt>Shipment status:</dt>
          <dd>
            <StatusBadge
              status={props.additionalOrderStates.displayStatus}
              id={`retailer_order_summary_state_${order.id}`}
              simplify
            />
          </dd>
          {props.additionalOrderStates.infoStatus?.length > 0 && (
            <>
              <dt>Info:</dt>
              <dd>
                <StatusBadge
                  status={props.additionalOrderStates.infoStatus}
                  id={`retailer_order_summary_info_state_${order.id}`}
                  simplify
                  additionalInfo
                />
              </dd>
            </>
          )}
        </dl>
        <dl>
          <dt>Type:</dt>
          <dd>{orderTypeFormatter(type)}</dd>
          <dt>Total Order Value:</dt>
          <dd>{currencyFormat(totalValue, currency)}</dd>
          <dt>Number of Units / Cases:</dt>
          <dd>{totalQuantity}</dd>
        </dl>
        <dl>
          <dt>Shipping &amp; Handling:</dt>
          <dd>{currencyFormat(totalShipping, currency)}</dd>

          {store && (
            <>
              <dt>Store:</dt>
              <dd>{store.name}</dd>
            </>
          )}
        </dl>
      </div>

      <div className={styles.list}>
        {showTrackingNumbers && (
          <dl className={styles.tracking_number}>
            <dt>Tracking numbers:</dt>
            <dd>
              {trackingNumbers.map((number) => (
                <span key={number}>{number} </span>
              ))}
            </dd>
          </dl>
        )}

        {props.additionalOrderStates.postShipmentStatus && (
          <dl className={styles.tracking_number}>
            <dt>Post-Shipment:</dt>
            <dd>
              <StatusBadge
                status={props.additionalOrderStates.postShipmentStatus}
                id={`retailer_order_summary_post_shipment_state_${order.id}`}
                simplify
                postShipmentStatus
              />
            </dd>
          </dl>
        )}
      </div>
      <hr />

      <BootstrapTable
        data={rows}
        columns={columns}
        keyField="id"
        striped
        bordered={false}
        hover
        noDataIndication={noDataIndication}
      ></BootstrapTable>

      <div className={styles.footer}>
        <Button text="Close" onClick={onClose} />
      </div>
    </ModalWindow>
  );
}

export default OrderSummaryModal;

OrderSummaryModal.defaultProps = {
  opened: false,
};

OrderSummaryModal.propTypes = {
  opened: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  order: PropTypes.any,
  suppliersNames: PropTypes.object,
};
